// Chat API Slice - LLM Chat endpoints with proper token management
import { createApi } from '@reduxjs/toolkit/query/react';
import { getApiBaseUrl } from '../../../Features/API/api';
import { baseQueryWithReauth } from '../../../Features/API/baseQueryWithReauth';

const BASE_URL = getApiBaseUrl();

export const chatApiSlice = createApi({
  reducerPath: 'chatApi',
  baseQuery: baseQueryWithReauth(BASE_URL),
  tagTypes: ['Conversation', 'Message', 'Provider'],
  endpoints: (builder) => ({
    // ========== CONVERSATION MANAGEMENT ==========
    createConversation: builder.mutation({
      query: (data = {}) => ({
        url: 'llm/conversations/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Conversation'],
    }),

    fetchConversations: builder.query({
      query: () => 'llm/conversations/',
      providesTags: ['Conversation'],
    }),

    fetchConversation: builder.query({
      query: (conversationId) => `llm/conversations/${conversationId}/`,
      providesTags: (result, error, conversationId) => [
        { type: 'Conversation', id: conversationId },
      ],
    }),

    deleteConversation: builder.mutation({
      query: (conversationId) => ({
        url: `llm/conversations/${conversationId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Conversation'],
    }),

    // ========== CHAT MESSAGING ==========
    sendMessage: builder.mutation({
      query: ({ conversationId, ...messageData }) => ({
        url: 'llm/chat/',
        method: 'POST',
        body: {
          conversation_id: conversationId,
          ...messageData,
        },
      }),
      invalidatesTags: (result, error, { conversationId }) => [
        { type: 'Conversation', id: conversationId },
        'Message',
      ],
    }),

    // Streaming endpoint - uses fetch with ReadableStream for POST support
    streamMessage: builder.mutation({
      queryFn: async ({ conversationId, message, onChunk, ...options }, api) => {
        try {
          // Get the token from state
          const token = api.getState().auth.token || localStorage.getItem('access');

          // Clean BASE_URL (remove trailing slash if present)
          const cleanBaseUrl = BASE_URL.endsWith('/') ? BASE_URL.slice(0, -1) : BASE_URL;

          // Make POST request with streaming
          const response = await fetch(`${cleanBaseUrl}/llm/chat/`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              conversation_id: conversationId,
              message,
              stream: true,
              temperature: options.temperature || 0.7,
              max_tokens: options.max_tokens || 2000,
            }),
          });

          if (!response.ok) {
            return {
              error: {
                status: response.status,
                data: `HTTP ${response.status}: ${response.statusText}`,
              },
            };
          }

          // Read streaming response
          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let fullResponse = '';
          let buffer = '';
          let chunkCount = 0;

          console.log('📡 Starting to read stream...');

          while (true) {
            const { done, value } = await reader.read();

            if (done) {
              console.log(
                '✅ Stream done, total chunks:',
                chunkCount,
                'total length:',
                fullResponse.length
              );
              break;
            }

            // Decode and buffer
            const decoded = decoder.decode(value, { stream: true });
            buffer += decoded;
            console.log('📦 Received chunk:', decoded.substring(0, 100));

            // Process complete SSE events
            const events = buffer.split('\n\n');
            buffer = events.pop() || '';

            for (const event of events) {
              if (event.startsWith('data: ')) {
                try {
                  const jsonData = JSON.parse(event.substring(6));
                  console.log('📨 Parsed SSE event:', jsonData);
                  if (jsonData.content) {
                    fullResponse += jsonData.content;
                    chunkCount++;
                    console.log(
                      '💬 Calling onChunk with length:',
                      fullResponse.length,
                      'onChunk type:',
                      typeof onChunk
                    );
                    if (onChunk) {
                      console.log(
                        '🎯 Actually calling onChunk now with:',
                        fullResponse.substring(0, 50)
                      );
                      onChunk(fullResponse);
                      console.log('✓ onChunk call completed');
                    } else {
                      console.warn('⚠️ onChunk is null/undefined!');
                    }
                  }
                } catch (e) {
                  console.warn('SSE parse error:', e, 'Event:', event.substring(0, 100));
                }
              }
            }
          }

          console.log('🏁 Returning final response, length:', fullResponse.length);
          return { data: { content: fullResponse, conversation_id: conversationId } };
        } catch (error) {
          return { error: { status: 'FETCH_ERROR', data: error.message } };
        }
      },
      invalidatesTags: (result, error, { conversationId }) => [
        { type: 'Conversation', id: conversationId },
        'Message',
      ],
    }),

    // ========== PROVIDER MANAGEMENT ==========
    fetchProviderStatus: builder.query({
      query: () => 'llm/providers/status/',
      providesTags: ['Provider'],
    }),

    fetchModels: builder.query({
      query: (provider) => ({
        url: 'llm/models/',
        params: provider ? { provider } : {},
      }),
      providesTags: ['Provider'],
    }),

    // ========== HEALTH CHECK ==========
    checkHealth: builder.query({
      query: () => 'llm/health/',
      // Don't cache health checks
      keepUnusedDataFor: 0,
    }),
  }),
});

// Export hooks for usage in functional components
export const {
  useCreateConversationMutation,
  useFetchConversationsQuery,
  useFetchConversationQuery,
  useDeleteConversationMutation,
  useSendMessageMutation,
  useStreamMessageMutation,
  useFetchProviderStatusQuery,
  useFetchModelsQuery,
  useCheckHealthQuery,
} = chatApiSlice;
